import { GraphQLClient } from 'graphql-request';

import config from '@/config';
import { urlToDatoLocale } from '@/lib/utils';
import {
  type ArticleListData,
  getArticleListQueryFields,
  listArticleFragment,
} from '@/lib/utils/blog';
import { type Language, type Region } from '@/types';
import { responsiveImageFragment } from './fragments';
import { type DatoBlogCategory, type DatoRegion } from './types';

type IdsQuery = {
  region: Pick<DatoRegion, 'id'>;
  allBlogCategories: Array<Pick<DatoBlogCategory, 'id'>>;
};

type FilteredArticlesQuery = Omit<
  ArticleListData<'articles'>,
  'allBlogCategories'
>;

type RequestParams = {
  query: string;
  variables?: Record<string, unknown>;
  preview?: boolean;
};

type RegionQuery = {
  region: {
    id: string;
  };
};

type Headers = {
  Authorization: `Bearer ${string}`;
  'X-Environment': string;
  'X-Exclude-Invalid': `${boolean}`;
  'X-Include-Drafts'?: `${boolean}`;
};

export function request<TQuery>({
  preview = false,
  query,
  variables = {},
}: RequestParams): Promise<TQuery> {
  const headers: Headers = {
    Authorization: `Bearer ${config.datocms.readToken}`,
    'X-Exclude-Invalid': 'true',
    'X-Environment': config.datocms.environment,
  };

  if (preview) {
    headers['X-Include-Drafts'] = 'true';
  }

  const client = new GraphQLClient('https://graphql.datocms.com', {
    fetch,
    headers,
    // @ts-expect-error -- Next's custom fetch option to cache for 4 hours
    next: { revalidate: 4 * 60 * 60 },
  });
  return client.request<TQuery>(query, variables);
}

export async function fetchBlogArticles(
  region: Region,
  language: Language,
  page: number,
  pageSize: number,
  categories: string[],
  filterMode: 'and' | 'or'
): Promise<FilteredArticlesQuery> {
  const idsQuery = `
    query GetRegionByCodeQuery($region: String!, $categories: [String]) {
      region(
        filter: {
          code: { eq: $region },
        },
      ) {
        id
      }
      allBlogCategories(
        filter: {
          code: { in: $categories },
        },
      ) {
        id
      }
    }
  `;

  const idsResult = await request<IdsQuery>({
    query: idsQuery,
    variables: { categories, region },
  });
  const regionId = idsResult.region.id;
  const categoryIds = idsResult.allBlogCategories.map((c) => c.id);
  const skip = page ? page * pageSize - pageSize : 0;
  const locale = urlToDatoLocale[`${region}${language}`];
  const query = `
    query FilteredArticlesQuery(
      $categories: [ItemId],
      $locale: SiteLocale!,
      $pageSize: IntType!,
      $regionId: ItemId!,
      $skip: IntType!,
    ) {
      ${getArticleListQueryFields({ articleAlias: 'articles', filterMode })}
    }
    ${listArticleFragment}
    ${responsiveImageFragment}
  `;

  const { articleCount, articles } = await request<FilteredArticlesQuery>({
    query,
    variables: { categories: categoryIds, locale, pageSize, regionId, skip },
  });

  return { articleCount, articles };
}

export const getRegionId = async (region: Region, preview = false) => {
  const regionQuery = `
    query RegionQuery($region: String!) {
      region(filter: { code: { eq: $region } }) {
        id
      }
    }
  `;

  const regionResult = await request<RegionQuery>({
    preview,
    query: regionQuery,
    variables: { region },
  });

  return regionResult.region.id;
};
